import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import KidButton from "@/components/KidButton";
import ShareButton from "@/components/ShareButton";
import { loadProgress, awardBadge } from "@/lib/progress";
import { printCertificate } from "@/lib/printCertificate";

// Auto-show the "Globe Trotter" certificate modal when the child crosses
// 20 learned countries AND 20+ stars. Shown once per device.
const SEEN_KEY = "we_globe_trotter_seen";

export default function CertificateModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [snap, setSnap] = useState({ countries: 0, stars: 0 });

  useEffect(() => {
    const check = () => {
      const p = loadProgress();
      const seen = localStorage.getItem(SEEN_KEY) === "1";
      if (!seen && p.countriesLearned.length >= 20 && p.stars >= 20) {
        awardBadge("globe-trotter", "Globe Trotter");
        setSnap({ countries: p.countriesLearned.length, stars: p.stars });
        setOpen(true);
        localStorage.setItem(SEEN_KEY, "1");
      }
    };
    check();
    const handler = () => check();
    window.addEventListener("we-progress-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("we-progress-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const close = () => setOpen(false);
  const certRef = useRef(null);
  const print = () => {
    printCertificate({
      innerHtml: certRef.current ? certRef.current.outerHTML : "",
      title: "Globe Trotter — My World Quest Certificate",
      url: "https://myworld.quest",
      footerText: "Scan to visit\nMy World Quest",
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] bg-slate-900/50 flex items-center justify-center p-4 print:bg-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            initial={{ scale: 0.7, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="cert-modal"
            ref={certRef}
            className="print-target relative w-full max-w-lg rounded-3xl border-4 border-slate-900 bg-yellow-100 p-8 md:p-10 shadow-[10px_10px_0_0_rgba(15,23,42,1)] text-center print:shadow-none print:border-2 print:max-w-full print:p-12"
          >
            {/* Decorative bursts (hidden on print so the certificate looks formal) */}
            <div className="absolute -top-4 -left-4 text-5xl print:hidden">🎉</div>
            <div className="absolute -top-4 -right-4 text-5xl print:hidden">🌟</div>
            <div className="absolute -bottom-4 -left-4 text-5xl print:hidden">🏆</div>
            <div className="absolute -bottom-4 -right-4 text-5xl print:hidden">🎊</div>

            {/* Full brand lockup — carries onto the printed certificate */}
            <img
              src="/brand-mark-full.webp"
              alt="My World Quest — Explore, Play, Learn, Grow"
              data-testid="cert-brand-full"
              className="mx-auto h-20 md:h-24 print:h-32 w-auto object-contain"
              loading="eager"
            />

            <div className="mt-3 text-6xl">🎓</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-2">{t("certificate.title")}</h2>
            <p className="mt-3 text-base md:text-lg font-bold text-slate-700">
              {t("certificate.body", { n: snap.countries })}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border-[3px] border-slate-900 bg-white font-bold">
              ⭐ {snap.stars} • 🌍 {snap.countries}
            </div>
            {/* Awarded date — adds a real-certificate feel on print */}
            <p className="mt-3 text-xs md:text-sm font-bold text-slate-600 uppercase tracking-widest">
              Awarded {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 print:hidden">
              <KidButton variant="yellow" size="lg" onClick={print} data-testid="cert-print">
                {t("certificate.print")}
              </KidButton>
              <ShareButton
                path="/"
                title="My World Quest"
                label={t("certificate.shareSubtitle", "I just earned my Globe Trotter badge!")}
                variant="solid"
                triggerLabel={t("certificate.share", "Share with a friend")}
                testId="cert-share"
              />
              <KidButton variant="white" size="lg" onClick={close} data-testid="cert-close">
                {t("certificate.keep")}
              </KidButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
